<template>
  <div>
    <div class="ma-page-head">
      <div>
        <h1 class="ma-page-title">
          Free Sessions
        </h1>
        <p class="ma-page-sub">
          Publiez des sessions (lien vidéo YouTube ou PDF) par niveau. Chaque étudiant voit celles de son niveau.
        </p>
      </div>
      <button
        class="ma-btn ma-btn-primary"
        @click="openCreate"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
        />
        Nouvelle Free Session
      </button>
    </div>

    <div class="ma-toolbar">
      <select
        v-model="levelFilter"
        class="ma-select"
      >
        <option value="">
          Tous les niveaux
        </option>
        <option
          v-for="l in levels"
          :key="l"
          :value="l"
        >
          {{ l }}
        </option>
      </select>
    </div>

    <div
      v-if="loading"
      class="ma-loading"
    >
      Chargement…
    </div>

    <div
      v-else-if="!filtered.length"
      class="ma-card ma-empty"
    >
      <div class="ma-empty-ico">
        <feather-icon
          icon="ZapIcon"
          size="30"
        />
      </div>
      <h4>{{ sessions.length ? 'Aucun résultat' : 'Aucune Free Session' }}</h4>
      <p>{{ sessions.length ? 'Ajuste le filtre de niveau.' : 'Ajoute ta première Free Session (vidéo ou PDF).' }}</p>
    </div>

    <div
      v-else
      class="fs-list"
    >
      <div
        v-for="s in filtered"
        :key="s._id"
        class="fs-row"
      >
        <div
          class="fs-ico"
          :class="s.type"
        >
          <feather-icon
            :icon="s.type === 'pdf' ? 'FileTextIcon' : 'YoutubeIcon'"
            size="20"
          />
        </div>
        <div class="fs-info">
          <h4>{{ s.title }}</h4>
          <div class="fs-tags">
            <span class="ma-badge level">{{ s.level }}</span>
            <span
              v-if="s.subject"
              class="ma-badge subject"
            >{{ s.subject }}</span>
            <span class="fs-type">{{ s.type === 'pdf' ? 'PDF' : 'Vidéo' }}</span>
          </div>
          <p
            v-if="s.description"
            class="fs-desc"
          >
            {{ s.description }}
          </p>
        </div>
        <div class="fs-actions">
          <a
            v-if="s.type === 'pdf'"
            :href="s.file && (apiBase + s.file.fileUrl)"
            target="_blank"
            rel="noopener"
            class="ma-btn ma-btn-ghost"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="15"
            />
            Ouvrir
          </a>
          <a
            v-else
            :href="watchUrl(s)"
            target="_blank"
            rel="noopener"
            class="ma-btn ma-btn-ghost"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="15"
            />
            Voir
          </a>
          <button
            class="fs-del"
            title="Supprimer"
            @click="remove(s)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="16"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal création -->
    <div
      v-if="showModal"
      class="ma-modal-backdrop"
      @click.self="showModal = false"
    >
      <div class="ma-modal">
        <div class="ma-modal-head">
          <h3 class="ma-modal-title">
            Nouvelle Free Session
          </h3>
          <p class="ma-modal-sub">
            Choisis un niveau, un type de contenu puis renseigne le lien ou le fichier.
          </p>
        </div>
        <div class="ma-modal-body">
          <div class="ma-field">
            <label>Titre <span class="req">*</span></label>
            <input
              v-model="form.title"
              class="ma-input"
              type="text"
              placeholder="Ex : Free Session Algo — révision DS"
            >
          </div>

          <div class="ma-row">
            <div class="ma-field">
              <label>Niveau <span class="req">*</span></label>
              <select
                v-model="form.level"
                class="ma-input"
              >
                <option value="">
                  — Choisir —
                </option>
                <option
                  v-for="l in levels"
                  :key="l"
                  :value="l"
                >
                  {{ l }}
                </option>
              </select>
            </div>
            <div class="ma-field">
              <label>Matière</label>
              <select
                v-model="form.subject"
                class="ma-input"
              >
                <option value="">
                  — Optionnel —
                </option>
                <option
                  v-for="s in subjects"
                  :key="s"
                  :value="s"
                >
                  {{ s }}
                </option>
              </select>
            </div>
          </div>

          <div class="ma-field">
            <label>Type de contenu <span class="req">*</span></label>
            <div class="fs-typeswitch">
              <button
                type="button"
                class="fs-typebtn"
                :class="{ active: form.type === 'video' }"
                @click="form.type = 'video'"
              >
                <feather-icon
                  icon="YoutubeIcon"
                  size="16"
                />
                Lien vidéo
              </button>
              <button
                type="button"
                class="fs-typebtn"
                :class="{ active: form.type === 'pdf' }"
                @click="form.type = 'pdf'"
              >
                <feather-icon
                  icon="FileTextIcon"
                  size="16"
                />
                Fichier PDF
              </button>
            </div>
          </div>

          <div
            v-if="form.type === 'video'"
            class="ma-field"
          >
            <label>Lien de la vidéo (YouTube) <span class="req">*</span></label>
            <input
              v-model="form.videoUrl"
              class="ma-input"
              type="text"
              placeholder="https://www.youtube.com/watch?v=…"
            >
          </div>

          <div
            v-else
            class="ma-field"
          >
            <label>Fichier PDF <span class="req">*</span></label>
            <input
              ref="fileInput"
              class="ma-input"
              type="file"
              accept="application/pdf"
              @change="onFile"
            >
            <small
              v-if="pdfFile"
              style="color:var(--ma-muted);font-size:0.78rem;"
            >{{ pdfFile.name }}</small>
          </div>

          <div class="ma-field">
            <label>Description</label>
            <textarea
              v-model="form.description"
              class="ma-input"
              rows="3"
              placeholder="Optionnel — quelques mots sur cette session."
            />
          </div>

          <p
            v-if="error"
            style="display:flex;align-items:center;gap:8px;color:#DC2626;background:#FEF2F2;padding:10px 14px;border-radius:10px;font-size:0.85rem;font-weight:600;"
          >
            {{ error }}
          </p>
        </div>
        <div class="ma-modal-foot">
          <button
            class="ma-btn ma-btn-ghost"
            @click="showModal = false"
          >
            Annuler
          </button>
          <button
            class="ma-btn ma-btn-primary"
            :disabled="saving"
            @click="create"
          >
            <span
              v-if="saving"
              class="ma-loader"
            />
            <span v-else>Publier</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import axios from '@axios'
import { LEVELS, SUBJECTS } from './constants'

export default {
  name: 'MindsAdminFreeSessions',
  data() {
    return {
      apiBase: process.env.VUE_APP_API_URL || '',
      sessions: [],
      loading: true,
      levelFilter: '',
      levels: LEVELS,
      subjects: SUBJECTS,
      showModal: false,
      form: {
        title: '', level: '', subject: '', type: 'video', videoUrl: '', description: '',
      },
      pdfFile: null,
      saving: false,
      error: '',
    }
  },
  computed: {
    filtered() {
      if (!this.levelFilter) return this.sessions
      return this.sessions.filter(s => s.level === this.levelFilter)
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        const res = await axios.get('/ma/admin/free-sessions')
        this.sessions = res.data.sessions || []
      } catch (e) {
        this.sessions = []
      } finally {
        this.loading = false
      }
    },
    watchUrl(s) {
      if (s.videoId) return `https://www.youtube.com/watch?v=${s.videoId}`
      return s.videoUrl || '#'
    },
    onFile(e) {
      const f = e.target.files && e.target.files[0]
      this.pdfFile = f || null
    },
    openCreate() {
      this.form = {
        title: '', level: '', subject: '', type: 'video', videoUrl: '', description: '',
      }
      this.pdfFile = null
      this.error = ''
      this.showModal = true
    },
    async create() {
      if (this.saving) return
      if (!this.form.title.trim()) { this.error = 'Le titre est requis.'; return }
      if (!this.form.level) { this.error = 'Le niveau est requis.'; return }
      if (this.form.type === 'video' && !this.form.videoUrl.trim()) {
        this.error = 'Le lien de la vidéo est requis.'; return
      }
      if (this.form.type === 'pdf' && !this.pdfFile) {
        this.error = 'Le fichier PDF est requis.'; return
      }
      this.saving = true
      this.error = ''
      try {
        let res
        if (this.form.type === 'pdf') {
          const fd = new FormData()
          fd.append('title', this.form.title.trim())
          fd.append('type', 'pdf')
          fd.append('level', this.form.level)
          fd.append('subject', this.form.subject || '')
          fd.append('description', this.form.description || '')
          fd.append('file', this.pdfFile)
          res = await axios.post('/ma/admin/free-sessions', fd)
        } else {
          res = await axios.post('/ma/admin/free-sessions', {
            title: this.form.title.trim(),
            type: 'video',
            level: this.form.level,
            subject: this.form.subject || '',
            description: this.form.description || '',
            videoUrl: this.form.videoUrl.trim(),
          })
        }
        this.sessions.unshift(res.data.session)
        this.showModal = false
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || 'Publication impossible.'
      } finally {
        this.saving = false
      }
    },
    async remove(s) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Supprimer « ${s.title} » ?`)) return
      try {
        await axios.delete(`/ma/admin/free-sessions/${s._id}`)
        this.sessions = this.sessions.filter(x => x._id !== s._id)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert((e.response && e.response.data && e.response.data.error) || 'Suppression impossible.')
      }
    },
  },
}
</script>

<style src="./minds.css"></style>
<style scoped>
.fs-list { display: flex; flex-direction: column; gap: 12px; }
.fs-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid var(--ma-line);
  border-radius: var(--ma-radius);
  box-shadow: var(--ma-shadow);
}
.fs-ico {
  width: 46px;
  height: 46px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 13px;
}
.fs-ico.video { background: #fee2e2; color: #dc2626; }
.fs-ico.pdf { background: #e0e7ff; color: #4f46e5; }
.fs-info { flex: 1; min-width: 0; }
.fs-info h4 { margin: 0 0 7px; font-size: 1rem; font-weight: 800; }
.fs-tags { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
.fs-type {
  padding: 2px 9px;
  border-radius: 20px;
  background: #f1f3f6;
  color: var(--ma-muted);
  font-size: 0.7rem;
  font-weight: 800;
}
.fs-desc { margin: 8px 0 0; color: var(--ma-muted); font-size: 0.85rem; line-height: 1.45; }
.fs-actions { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.fs-del {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid var(--ma-line);
  border-radius: 11px;
  background: #fff;
  color: var(--ma-muted);
  cursor: pointer;
  transition: 0.15s ease;
}
.fs-del:hover { border-color: #fecaca; background: #fff1f2; color: #dc2626; }

.fs-typeswitch { display: flex; gap: 10px; }
.fs-typebtn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 14px;
  border: 1.5px solid var(--ma-line);
  border-radius: 12px;
  background: #fff;
  color: var(--ma-muted-strong);
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: 0.15s ease;
}
.fs-typebtn.active {
  border-color: var(--ma-teal);
  background: var(--ma-teal-soft);
  color: var(--ma-teal-dark);
}

@media (max-width: 640px) {
  .fs-row { flex-wrap: wrap; }
  .fs-actions { width: 100%; justify-content: flex-end; }
}
</style>
